import React from 'react';
import { AIStats as AIStatsType } from '../services/zodiacEngine/AIController';
import styles from './AIStats.module.css';

interface AIStatsProps {
  aiStats: AIStatsType | null;
}

/**
 * Component to display AI performance statistics
 */
const AIStats: React.FC<AIStatsProps> = ({ aiStats }) => {
  // Detect dark mode by checking if the parent container has the 'dark' class
  // (BoardContainer applies .container.dark in dark mode)
  // We'll use a simple approach: check if document.body or a parent has 'dark' class
  const [isDark, setIsDark] = React.useState(false);
  React.useEffect(() => {
    // Find the closest parent with class 'container' and check for 'dark'
    const checkDark = () => {
      let el = document.querySelector('.container');
      if (el && el.classList.contains('dark')) setIsDark(true);
      else setIsDark(false);
    };
    checkDark();
    // Listen for class changes (in case of theme toggle)
    const observer = new MutationObserver(checkDark);
    const container = document.querySelector('.container');
    if (container) observer.observe(container, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (!aiStats) {
    return <div className={styles.aiStats + (isDark ? ' ' + styles.dark : '')}>No AI statistics available</div>;
  }

  // Convert Map to array of [depth, count] entries for rendering
  const depthEntries = Array.from(aiStats.nodesByDepth.entries())
    .sort((a, b) => a[0] - b[0]); // Sort by depth

  // Calculate the max depth reached
  const maxDepth = depthEntries.length > 0 ? depthEntries[depthEntries.length - 1][0] : 0;

  // Get the deepest few entries where there are significant nodes
  const significantDepthEntries = depthEntries.filter(([_, count]) => count > 10);
  const deepestSignificantDepth = significantDepthEntries.length > 0 ? 
    significantDepthEntries[significantDepthEntries.length - 1][0] : 0;

  return (
    <div className={styles.aiStats + (isDark ? ' ' + styles.dark : '')}>
      <h3>AI Statistics</h3>
      
      <div>
        <strong>Time:</strong> {aiStats.executionTimeMs}ms
      </div>
      <div>
        <strong>Total Nodes:</strong> {aiStats.totalNodesVisited.toLocaleString()}
      </div>
      <div>
        <strong>Max Depth:</strong> {maxDepth} (Significant: {deepestSignificantDepth})
      </div>
      <div>
        <strong>Heuristic:</strong> {aiStats.heuristicUsed || "None"}
      </div>
      
      <div className={styles.nodesByDepthSection}>
        <strong>Nodes by Depth:</strong>
        <div className={styles.nodesByDepthTableWrapper}>
          <table className={styles.nodesByDepthTable}>
            <thead>
              <tr>
                <th>Depth</th>
                <th>Nodes</th>
                <th>%</th>
                <th>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {depthEntries.map(([depth, count]) => {
                const percentage = aiStats.totalNodesVisited 
                  ? (count / aiStats.totalNodesVisited) * 100
                  : 0;
                // Color gradient for light/dark
                const depthColor = isDark
                  ? (depth < 3 ? '#223344' : depth < 5 ? '#224466' : depth < 7 ? '#225577' : depth < 9 ? '#226688' : '#227799')
                  : (depth < 3 ? '#e6f7ff' : depth < 5 ? '#ccefff' : depth < 7 ? '#99dfff' : depth < 9 ? '#66cfff' : '#33bfff');
                return (
                  <tr key={depth} style={{ backgroundColor: count > 0 ? depthColor : 'inherit', fontWeight: depth > 5 ? 'bold' : 'normal' }}>
                    <td>{depth}</td>
                    <td>{count.toLocaleString()}</td>
                    <td>{percentage.toFixed(1)}%</td>
                    <td>
                      <div style={{
                        width: `${Math.min(Math.max(percentage * 2, 2), 100)}%`, // never more than 100%
                        maxWidth: '100%',
                        height: '8px',
                        backgroundColor: isDark ? '#4fa3ff' : '#007bff',
                        borderRadius: '2px'
                      }} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AIStats; 